import { Component, OnInit } from '@angular/core';
import { DataAccessFairsFacade } from '@planone/bau365/data-access-fair-data';
import {
  NewsfeedContentSource,
  NewsfeedPageFacade,
  NewsfeedQuicklinksFacade,
  NewsfeedResult,
  NewsfeedResultsFacade,
  Quicklink,
} from '@planone/bau365/data-access-newsfeed';
import { DataAccessScreensizeFacade } from '@planone/bau365/data-access-screensize';
import { DataAccessSearchFacade } from '@planone/bau365/data-access-search';
import { Channel, NewsfeedChannelsFacade } from '@planone/bau365/data-access-user';
import { UserHistoryFacade } from '@planone/bau365/data-access-user';
import { LinkClickFacade } from '@planone/bau365/feature-tracking';
import { DataAccessTogglesFacade } from '@planone/shared/data-access-feature-flags';
import { localizedMeta, MetaService } from '@planone/shared/util-meta';
import { KeycloakService } from 'keycloak-angular';
import { filter, map, Observable } from 'rxjs';

@Component({
  selector: 'bau365-page-newsfeed',
  templateUrl: './page-newsfeed.component.html',
  styleUrls: ['./page-newsfeed.component.scss'],
})
export class PageNewsfeedComponent implements OnInit {
  givenName = '';
  familyName = '';

  showMobileBottomBar$: Observable<boolean | undefined>;
  results$: Observable<NewsfeedResult[]>;
  resultsCount$: Observable<number>;
  moreResultsAvailable$: Observable<boolean>;
  resultsLoaded$: Observable<boolean>;
  isLoadingMoreResults$: Observable<boolean>;
  isHistoryChannelAvailable$: Observable<boolean>;
  insightsId$: Observable<string>;
  size$: Observable<string | undefined>;
  source$: Observable<NewsfeedContentSource>;
  searchTerm$: Observable<string>;
  searchSuggestions$: Observable<string[]>;
  sponsoredChannels$: Observable<Channel[]>;
  subscribedChannels$: Observable<Channel[]>;
  quicklinks$: Observable<Quicklink[]>;

  constructor(
    private keycloakService: KeycloakService,
    private newsfeedPageFacade: NewsfeedPageFacade,
    private linkClickTracking: LinkClickFacade,
    newsfeedResultsFacade: NewsfeedResultsFacade,
    newsfeedChannelsFacade: NewsfeedChannelsFacade,
    fairsFacade: DataAccessFairsFacade,
    screenSizeFacade: DataAccessScreensizeFacade,
    searchFacade: DataAccessSearchFacade,
    quicklinksFacade: NewsfeedQuicklinksFacade,
    togglesFacade: DataAccessTogglesFacade,
    userHistoryFacade: UserHistoryFacade,
    metaService: MetaService
  ) {
    metaService.setTitle(localizedMeta['titleNewsfeed']);
    this.results$ = newsfeedResultsFacade.results$;
    this.resultsCount$ = newsfeedResultsFacade.resultsCount$;
    this.moreResultsAvailable$ = newsfeedResultsFacade.moreAvailable$;
    this.resultsLoaded$ = newsfeedResultsFacade.isLoaded$;
    this.sponsoredChannels$ = newsfeedChannelsFacade.sponsoredChannels$;
    this.subscribedChannels$ = newsfeedChannelsFacade.subscribedChannels$;
    this.insightsId$ = fairsFacade.insightsId$;
    this.size$ = screenSizeFacade.size$;
    this.source$ = newsfeedResultsFacade.source$;
    this.searchTerm$ = this.newsfeedPageFacade.searchTermFragment$;
    this.searchSuggestions$ = searchFacade.autocompleteSuggestions$.pipe(
      filter(Boolean),
      map((suggestions) => suggestions.map(({ suggestion }) => suggestion))
    );
    this.quicklinks$ = quicklinksFacade.allNewsfeedQuicklinks$;
    this.isLoadingMoreResults$ = newsfeedResultsFacade.isLoadingMore$.pipe(map((isLoadingMore) => !!isLoadingMore));
    this.isHistoryChannelAvailable$ = userHistoryFacade.userHistory$.pipe(map((entries) => entries.length > 0));
    this.showMobileBottomBar$ = togglesFacade.flag('show-mobile-bottom-bar');
  }

  ngOnInit(): void {
    this.newsfeedPageFacade.openPage();
    const instance = this.keycloakService.getKeycloakInstance();
    const token = instance.tokenParsed;
    if (token) {
      this.givenName = token['given_name'];
      this.familyName = token['family_name'];
    }
  }

  onLoadMoreResults(): void {
    this.newsfeedPageFacade.loadMore();
  }

  onSelectForMe(): void {
    this.newsfeedPageFacade.selectRootChannel();
  }

  onSelectChannel(channel: Channel): void {
    this.newsfeedPageFacade.selectSubscribedChannel(channel.id, channel.fairId);
  }

  onSelectLastViewed(): void {
    this.newsfeedPageFacade.selectLastViewedChannel();
  }

  onSearchTermChanged(searchTerm: string): void {
    this.newsfeedPageFacade.inputSearchTerm(searchTerm);
  }

  onSearchTermSubmitted(): void {
    this.newsfeedPageFacade.performSearch();
  }

  onQuicklinkClick(context: string, targetUrl: string) {
    this.linkClickTracking.trigger({
      context,
      targetUrl,
      pageType: 'newsfeed',
      linkType: 'QUICKLINK',
      entityType: 'QUICKLINK',
    });
  }
}
